import React, { useEffect, useState } from 'react';
import {
  Table, Button, Input, Space, Tag, Modal, App, Typography, Form,
  InputNumber, Descriptions, Row, Col, Avatar, Card
} from 'antd';
import {
  ReloadOutlined, StopOutlined, CheckCircleOutlined, DollarOutlined,
  EyeOutlined, UserOutlined, PhoneOutlined, MailOutlined,
  EnvironmentOutlined, ClockCircleOutlined, CrownOutlined, StarOutlined
} from '@ant-design/icons';
import api from '@/api/client';
import type { PaginatedResponse } from '@/types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface UserDetail {
  id: number;
  username: string;
  email: string;
  display_name: string;
  phone: string;
  avatar_url: string;
  industry: string;
  role: string;
  credits: number;
  total_recharge: number;
  is_early_user: number;
  is_coach: number;
  is_coach_verified: number;
  city: string;
  work_years: number;
  bio: string;
  status: string;
  user_source: string;
  identities: string[];
  product_stage: string;
  created_at: string;
  updated_at: string;
  recentTransactions?: Transaction[];
}

interface Transaction {
  id: number;
  amount: number;
  type: string;
  description: string;
  refType: string;
  balanceAfter: number;
  createdAt: string;
}

// 交易类型中文映射
const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  recharge: '充值',
  consume: '消费',
  refund: '退款',
  gift: '赠送',
  manual: '手动调整',
  freeze: '冻结',
  cancel_freeze: '取消冻结',
  feedback_reward: '反馈奖励',
  task_reward: '任务奖励',
  invite_reward: '邀请奖励',
  unknown: '未知',
};

const IDENTITY_LABELS: Record<string, string> = {
  regular: '普通用户',
  coach: '产品教练',
  super_pm: '超级产品经理',
  angel_pm: '天使产品',
};

const IDENTITY_COLORS: Record<string, string> = {
  regular: 'default',
  coach: 'blue',
  super_pm: 'gold',
  angel_pm: 'pink',
};

// 身份优先级（数字越大优先级越高）
const IDENTITY_PRIORITY: Record<string, number> = {
  regular: 0,
  coach: 10,
  angel_pm: 15,
  super_pm: 20,
};

const PRODUCT_STAGE_LABELS: Record<string, string> = {
  seed: '种子期',
  growth: '成长期',
  mature: '成熟期',
  enterprise: '企业版',
};

interface UserLevel {
  id: number;
  level: number;
  name: string;
  min_recharge: number;
  max_recharge: number | null;
  color: string;
}

// 用户等级定义（从数据库获取）
let USER_LEVELS_CACHE: UserLevel[] | null = null;

// 计算用户等级
const calculateUserLevel = (totalRecharge: number, levels: UserLevel[]) => {
  for (let i = levels.length - 1; i >= 0; i--) {
    if (totalRecharge >= levels[i].min_recharge) {
      return levels[i];
    }
  }
  return levels[0];
};

export default function UsersPage() {
  const [data, setData] = useState<PaginatedResponse<UserDetail>>({ items: [], total: 0, page: 1, pageSize: 20 });
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [creditModal, setCreditModal] = useState<{ open: boolean; user: UserDetail | null }>({ open: false, user: null });
  const [detailModal, setDetailModal] = useState<{ open: boolean; user: UserDetail | null }>({ open: false, user: null });
  const [userLevels, setUserLevels] = useState<UserLevel[]>([]);
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const loadUserLevels = async () => {
    if (USER_LEVELS_CACHE) {
      setUserLevels(USER_LEVELS_CACHE);
      return;
    }
    try {
      const { data } = await api.get('/user-levels');
      USER_LEVELS_CACHE = data;
      setUserLevels(data);
    } catch (err) {
      console.error('加载等级配置失败', err);
    }
  };

  const loadUsers = async (page = 1) => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/users', { params: { page, pageSize: 20, keyword } });
      setData(res);
    } catch (err) {
      message.error('加载用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    loadUsers(); 
    loadUserLevels();
  }, []);

  const handleDisable = async (id: number) => {
    try {
      await api.post(`/users/${id}/disable`);
      message.success('用户已禁用');
      loadUsers();
    } catch { message.error('操作失败'); }
  };

  const handleEnable = async (id: number) => {
    try {
      await api.post(`/users/${id}/enable`);
      message.success('用户已启用');
      loadUsers();
    } catch { message.error('操作失败'); }
  };

  const handleAdjustCredits = async () => {
    try {
      const values = await form.validateFields();
      await api.post(`/users/${creditModal.user!.id}/credits`, values);
      message.success('积分调整成功');
      setCreditModal({ open: false, user: null });
      form.resetFields();
      loadUsers();
    } catch { message.error('积分调整失败'); }
  };

  const showDetail = async (user: UserDetail) => {
    try {
      const { data: detail } = await api.get(`/users/${user.id}`);
      setDetailModal({ open: true, user: detail });
    } catch {
      message.error('加载用户详情失败');
    }
  };

  const renderPrimaryIdentity = (identities: string[] | string) => {
    const ids = Array.isArray(identities) ? identities : JSON.parse(identities || '[]');
    if (!ids || ids.length === 0) return '-';
    
    // 按优先级排序，取最高优先级
    const sortedIds = ids.sort((a: string, b: string) => 
      (IDENTITY_PRIORITY[b] || 0) - (IDENTITY_PRIORITY[a] || 0)
    );
    const primaryId = sortedIds[0];
    
    // 普通用户不显示标签
    if (primaryId === 'regular') return '-';
    
    return (
      <Tag color={IDENTITY_COLORS[primaryId] || 'default'} style={{ fontSize: 12 }}>
        {IDENTITY_LABELS[primaryId] || primaryId}
      </Tag>
    );
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '用户', dataIndex: 'username', width: 150,
      render: (_: string, record: UserDetail) => (
        <Space>
          <Avatar src={record.avatar_url} icon={<UserOutlined />} size="small" />
          <div>
            <div>{record.display_name || record.username}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>@{record.username}</Text>
          </div>
        </Space>
      ),
    },
    { title: '邮箱', dataIndex: 'email', width: 180, ellipsis: true },
    { title: '手机', dataIndex: 'phone', width: 120 },
    {
      title: '身份', dataIndex: 'identities', width: 120,
      render: (ids: string[] | string) => renderPrimaryIdentity(ids),
    },
    {
      title: '等级', width: 100,
      render: (_: any, record: UserDetail) => {
        if (userLevels.length === 0) return '-';
        const levelInfo = calculateUserLevel(Number(record.total_recharge) || 0, userLevels);
        return (
          <Tag color={levelInfo.color} style={{ fontSize: 12 }}>
            Lv.{levelInfo.level} {levelInfo.name}
          </Tag>
        );
      },
    },
    { title: '积分', dataIndex: 'credits', width: 80, align: 'right' as const },
    {
      title: '来源', dataIndex: 'user_source', width: 100,
      render: (s: string) => {
        const sourceMap: Record<string, { label: string; color: string }> = {
          local: { label: '本地', color: 'default' },
          wp: { label: 'WP', color: 'blue' },
          invite: { label: '邀请码', color: 'green' },
          wechat: { label: '微信', color: 'green' },
          gitee: { label: 'Gitee', color: 'red' },
        };
        const config = sourceMap[s] || { label: s, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '状态', dataIndex: 'status', width: 80, align: 'center' as const,
      render: (s: string) => <Tag color={s === 'active' ? 'green' : 'red'}>{s === 'active' ? '正常' : '禁用'}</Tag>,
    },
    {
      title: '注册时间', dataIndex: 'created_at', width: 120,
      render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
    },
    {
      title: '操作', width: 280, fixed: 'right' as const,
      render: (_: any, record: UserDetail) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => showDetail(record)}>详情</Button>
          {record.status === 'active' ? (
            <Button size="small" danger icon={<StopOutlined />} onClick={() => handleDisable(record.id)}>禁用</Button>
          ) : (
            <Button size="small" icon={<CheckCircleOutlined />} onClick={() => handleEnable(record.id)}>启用</Button>
          )}
          <Button size="small" icon={<DollarOutlined />} onClick={() => setCreditModal({ open: true, user: record })}>积分</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>用户管理</Title>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search placeholder="搜索用户名/邮箱" onSearch={() => loadUsers()} value={keyword} onChange={(e) => setKeyword(e.target.value)} style={{ width: 300 }} />
        <Button icon={<ReloadOutlined />} onClick={() => loadUsers()}>刷新</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={data.items}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          current: data.page,
          pageSize: data.pageSize,
          total: data.total,
          onChange: loadUsers,
          showSizeChanger: false,
        }}
      />

      {/* 积分调整弹窗 */}
      <Modal
        title={`调整积分 - ${creditModal.user?.username}`}
        open={creditModal.open}
        onCancel={() => { setCreditModal({ open: false, user: null }); form.resetFields(); }}
        onOk={handleAdjustCredits}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="amount" label="积分数量" rules={[{ required: true, message: '请输入积分数量' }]}>
            <InputNumber style={{ width: '100%' }} placeholder="正数增加，负数减少" />
          </Form.Item>
          <Form.Item name="description" label="调整说明">
            <Input.TextArea rows={2} placeholder="选填" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 用户详情弹窗 */}
      <Modal
        title="用户详情"
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false, user: null })}
        width={800}
        footer={null}
      >
        {detailModal.user && (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <Row gutter={16} align="middle">
                <Col>
                  <Avatar src={detailModal.user.avatar_url} icon={<UserOutlined />} size={64} />
                </Col>
                <Col flex="auto">
                  <Title level={5} style={{ margin: 0 }}>{detailModal.user.display_name || detailModal.user.username}</Title>
                  <Text type="secondary">@{detailModal.user.username}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color={detailModal.user.status === 'active' ? 'green' : 'red'}>
                      {detailModal.user.status === 'active' ? '正常' : '禁用'}
                    </Tag>
                    {detailModal.user.is_early_user === 1 && <Tag color="gold" icon={<StarOutlined />}>早期用户</Tag>}
                    {renderPrimaryIdentity(detailModal.user.identities)}
                    {(() => {
                      if (userLevels.length === 0) return null;
                      const levelInfo = calculateUserLevel(Number(detailModal.user.total_recharge) || 0, userLevels);
                      return (
                        <Tag color={levelInfo.color}>
                          Lv.{levelInfo.level} {levelInfo.name}
                        </Tag>
                      );
                    })()}
                  </div>
                </Col>
              </Row>
            </Card>

            <Row gutter={16}>
              <Col span={12}>
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label={<><MailOutlined /> 邮箱</>}>{detailModal.user.email || '-'}</Descriptions.Item>
                  <Descriptions.Item label={<><PhoneOutlined /> 手机</>}>{detailModal.user.phone || '-'}</Descriptions.Item>
                  <Descriptions.Item label="行业">{detailModal.user.industry || '-'}</Descriptions.Item>
                  <Descriptions.Item label="职位">{detailModal.user.role || '-'}</Descriptions.Item>
                  <Descriptions.Item label={<><EnvironmentOutlined /> 城市</>}>{detailModal.user.city || '-'}</Descriptions.Item>
                  <Descriptions.Item label="工作年限">{detailModal.user.work_years ? `${detailModal.user.work_years}年` : '-'}</Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={12}>
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="当前积分">{detailModal.user.credits}</Descriptions.Item>
                  <Descriptions.Item label="累计充值">¥{Number(detailModal.user.total_recharge || 0).toFixed(2)}</Descriptions.Item>
                  <Descriptions.Item label="用户来源">{
                    (() => {
                      const sourceMap: Record<string, string> = {
                        local: '本地注册',
                        wp: 'WordPress',
                        invite: '邀请码注册',
                        wechat: '微信登录',
                        gitee: 'Gitee登录',
                      };
                      return sourceMap[detailModal.user.user_source] || detailModal.user.user_source;
                    })()
                  }</Descriptions.Item>
                  <Descriptions.Item label={<><ClockCircleOutlined /> 注册时间</>}>{dayjs(detailModal.user.created_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
                  <Descriptions.Item label="更新时间">{dayjs(detailModal.user.updated_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            {detailModal.user.bio && (
              <Card size="small" title="个人简介" style={{ marginTop: 16 }}>
                {detailModal.user.bio}
              </Card>
            )}

            {/* 最近积分交易记录 */}
            {detailModal.user.recentTransactions && detailModal.user.recentTransactions.length > 0 && (
              <Card size="small" title="最近积分交易" style={{ marginTop: 16 }}>
                <Table
                  dataSource={detailModal.user.recentTransactions}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: '时间',
                      dataIndex: 'createdAt',
                      key: 'createdAt',
                      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
                    },
                    {
                      title: '类型',
                      dataIndex: 'type',
                      key: 'type',
                      render: (v) => {
                        const typeValue = v || 'unknown';
                        const color = typeValue.includes('reward') ? 'green' : typeValue === 'consume' ? 'red' : 'blue';
                        const label = TRANSACTION_TYPE_LABELS[typeValue] || typeValue;
                        return <Tag color={color}>{label}</Tag>;
                      },
                    },
                    {
                      title: '积分变化',
                      dataIndex: 'amount',
                      key: 'amount',
                      render: (v) => (
                        <Text style={{ color: v > 0 ? '#52c41a' : '#ff4d4f' }}>
                          {v > 0 ? `+${v}` : v}
                        </Text>
                      ),
                    },
                    {
                      title: '余额',
                      dataIndex: 'balanceAfter',
                      key: 'balanceAfter',
                      render: (v) => v ?? '-',
                    },
                    {
                      title: '说明',
                      dataIndex: 'description',
                      key: 'description',
                      ellipsis: true,
                      render: (v) => v || '-',
                    },
                  ]}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
